package com.islamicmedia.site.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import com.islamicmedia.shared.data.api.createDefaultApiClient
import com.islamicmedia.shared.platform.Event
import com.islamicmedia.shared.platform.Funnel
import com.islamicmedia.shared.platform.FunnelStep
import com.islamicmedia.shared.presentation.AuthUiState
import com.islamicmedia.shared.presentation.AuthViewModel
import com.islamicmedia.site.components.EmailVerificationModal
import com.islamicmedia.site.components.InjectStyles
import com.islamicmedia.site.util.Utm
import com.varabyte.kobweb.core.Page
import kotlin.math.PI
import kotlin.math.cos
import kotlin.math.sin
import kotlin.random.Random
import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.delay
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.*
import org.w3c.dom.HTMLElement
import org.w3c.dom.url.URLSearchParams

// Same terracotta/cream palette as the ThriveCart-styled /register page (since retired; it was TheFitrahFilterOffer.kt),
// for visual continuity with the ThriveCart checkout the buyer is arriving from. Duplicated rather
// than shared: those are file-private there, and this is the only other page that wants the look.
private val TcCream = rgb(250, 247, 240)
private val TcWhite = rgb(255, 255, 255)
private val TcNavy = rgb(16, 46, 71)
private val TcGold = rgb(201, 169, 97)
private val TcTeal = rgb(13, 118, 128)
private val TcInk = rgb(35, 35, 40)
private const val FontBody = "'DM Sans', Arial, sans-serif"
private const val FontHead = "'Poppins', Arial, sans-serif"
private const val ButtonRadius = "4.5px"

// ThriveCart appends these on its Thank You redirect (see "Accessing order information via the
// query string" in ThriveCart's docs) — bracketed keys like "thrivecart[customer][email]" are
// just literal URLSearchParams keys, no special array parsing needed. These values are UX-only
// (prefill), never trusted to grant access — the ThriveCart webhook (server-to-server, secret-
// verified) is the sole source of truth for entitlement. See ThriveCartRoutes.kt.
private const val TC_ORDER_KEY = "tc_order_id"
private const val TC_EMAIL_KEY = "tc_email"
private const val TC_FIRST_KEY = "tc_first"
private const val TC_LAST_KEY = "tc_last"
private const val TC_TS_KEY = "tc_ts"
// Same rationale as PURCHASE_STICKY_MS on /register: long enough to survive a Google OAuth
// round-trip, short enough that a stale browser doesn't get stuck here forever.
private const val TC_STICKY_MS = 30 * 60 * 1000.0

private fun clearStoredThriveCart() {
    window.localStorage.removeItem(TC_ORDER_KEY)
    window.localStorage.removeItem(TC_EMAIL_KEY)
    window.localStorage.removeItem(TC_FIRST_KEY)
    window.localStorage.removeItem(TC_LAST_KEY)
    window.localStorage.removeItem(TC_TS_KEY)
}

/**
 * Landing page for the "Thank You" redirect on the ThriveCart-hosted checkout at
 * learn.thegoodchild.org/the-fitrah-filter — a separate invitation page from our own /register
 * flow, still on ThriveCart rather than our Stripe Elements checkout. Access itself is granted by
 * ThriveCart's webhook (ThriveCartRoutes.kt) writing the same email-keyed `user_entitlements` row
 * the Stripe webhook writes; this page's only job is to get the buyer signed into an account with
 * that email and then hand them into the app once the grant has landed.
 */
@Page("/thrivecart-thank-you")
@Composable
fun ThriveCartThankYou() {
    LaunchedEffect(Unit) {
        val gfonts = kotlinx.browser.document.createElement("link")
        gfonts.setAttribute("rel", "stylesheet")
        gfonts.setAttribute(
            "href",
            "https://fonts.googleapis.com/css2?family=DM+Sans:ital,wght@0,400;0,500;0,700;1,400" +
                "&family=Poppins:wght@400;600;700&display=swap"
        )
        kotlinx.browser.document.head?.appendChild(gfonts)
    }

    var paidEmail by remember { mutableStateOf("") }
    var paidFirstName by remember { mutableStateOf("") }
    var paidLastName by remember { mutableStateOf("") }
    var orderId by remember { mutableStateOf<String?>(null) }

    LaunchedEffect(Unit) {
        val params = URLSearchParams(window.location.search)
        val oid = params.get("thrivecart[order_id]")?.takeIf { it.isNotBlank() }
        if (oid != null) {
            window.localStorage.setItem(TC_ORDER_KEY, oid)
            window.localStorage.setItem(TC_EMAIL_KEY, params.get("thrivecart[customer][email]").orEmpty())
            window.localStorage.setItem(TC_FIRST_KEY, params.get("thrivecart[customer][firstname]").orEmpty())
            window.localStorage.setItem(TC_LAST_KEY, params.get("thrivecart[customer][lastname]").orEmpty())
            window.localStorage.setItem(TC_TS_KEY, kotlin.js.Date.now().toString())
        }
        // A returning Google OAuth round-trip has no query string at all, so a recently-stored
        // order still counts — briefly, so an abandoned/bookmarked link doesn't pin this browser
        // to the post-payment screen forever.
        val storedFresh = window.localStorage.getItem(TC_ORDER_KEY)?.takeIf {
            val ts = window.localStorage.getItem(TC_TS_KEY)?.toDoubleOrNull() ?: 0.0
            kotlin.js.Date.now() - ts < TC_STICKY_MS
        }
        if (storedFresh == null) clearStoredThriveCart()
        orderId = oid ?: storedFresh
        paidEmail = params.get("thrivecart[customer][email]") ?: window.localStorage.getItem(TC_EMAIL_KEY).orEmpty()
        paidFirstName = params.get("thrivecart[customer][firstname]") ?: window.localStorage.getItem(TC_FIRST_KEY).orEmpty()
        paidLastName = params.get("thrivecart[customer][lastname]") ?: window.localStorage.getItem(TC_LAST_KEY).orEmpty()
        // Consumed — drop the buyer's name and address from storage immediately. They only had to
        // survive as far as this read; leaving them for the 30-minute sticky window would prefill
        // one buyer's details into the next person's form on a shared or family computer. The order
        // id and timestamp stay (needed across the OAuth round-trip) and identify nobody on their own.
        window.localStorage.removeItem(TC_EMAIL_KEY)
        window.localStorage.removeItem(TC_FIRST_KEY)
        window.localStorage.removeItem(TC_LAST_KEY)
        if (oid != null) window.history.replaceState(null, "", window.location.pathname)
        Funnel.track("thrivecart_thank_you_viewed", params = mapOf("page" to "thrivecart_thank_you"))
    }

    val authViewModel = remember { AuthViewModel() }
    val authState by authViewModel.uiState.collectAsState()
    val apiClient = remember { createDefaultApiClient() }

    var prefilled by remember { mutableStateOf(false) }
    var switchedToSignIn by remember { mutableStateOf(false) }
    var polling by remember { mutableStateOf(false) }
    var entitled by remember { mutableStateOf(false) }
    var pollTimedOut by remember { mutableStateOf(false) }

    LaunchedEffect(authState.error) {
        val err = authState.error?.lowercase()
        if (!switchedToSignIn && authState.isSignUp && err != null &&
            (err.contains("already registered") || err.contains("already exists") || err.contains("user already"))
        ) {
            switchedToSignIn = true
            authViewModel.toggleSignUp()
        }
    }

    // Save them retyping the address they just paid with — cosmetic prefill only. Whoever they
    // actually sign in/up as here is who the poll below checks, regardless of this value.
    LaunchedEffect(paidEmail, authState.email) {
        if (!prefilled && paidEmail.isNotBlank() && authState.email.isBlank()) {
            authViewModel.onEmailChanged(paidEmail)
            if (paidFirstName.isNotBlank()) authViewModel.onFirstNameChanged(paidFirstName)
            if (paidLastName.isNotBlank()) authViewModel.onLastNameChanged(paidLastName)
            prefilled = true
        }
    }

    // Poll the signed-in account's own entitlement once authenticated — no separate "claim" step
    // exists for ThriveCart (see file doc comment): if they sign up/in with the same email they
    // checked out with, the webhook's grant is just already sitting on this row.
    LaunchedEffect(authState.isAuthenticated, authState.isLoading) {
        if (authState.isLoading) return@LaunchedEffect
        if (!authState.isAuthenticated || entitled || polling) return@LaunchedEffect
        polling = true
        pollTimedOut = false
        // try/finally: without it, a cancellation mid-poll (the OAuth navigation, a recomposition)
        // leaves `polling` stuck true, and the guard above then blocks every future attempt —
        // stranding a paying buyer on "Activating…" with no way forward.
        try {
            var attempt = 0
            while (attempt < 18) { // ~36s — the webhook is usually near-instant, but give it room
                val ent = apiClient.fetchEntitlement()
                if (ent?.unlimitedUntil != null) {
                    entitled = true
                    break
                }
                attempt++
                delay(2000)
            }
        } finally {
            polling = false
            if (entitled) clearStoredThriveCart() else pollTimedOut = true
        }
    }

    LaunchedEffect(authState.isAuthenticated, entitled) {
        if (authState.isAuthenticated && entitled) {
            // Fired here, not on arrival: landing on this page only proves someone opened a URL,
            // whereas a granted entitlement proves the webhook saw a real payment. Keyed so a
            // reload of the same order can't count the conversion twice.
            val trackedKey = "tc_subscribed_${orderId ?: "unknown"}"
            if (window.localStorage.getItem(trackedKey) == null) {
                window.localStorage.setItem(trackedKey, "1")
                Funnel.track(
                    Event.SUBSCRIBED, FunnelStep.SUBSCRIBED,
                    mapOf("plan" to "thrivecart", "source" to "thrivecart_thank_you")
                )
            }
            // Straight into the app with no pause, already signed in — the Supabase session lives
            // in this browser, so /app picks it up with no second login.
            //
            // No delay on purpose: everyone here is creating a NEW account, so this only ever runs
            // after they submit the form — by which point the confetti (fired on page load, while
            // they were typing) has long finished. A pause here would just be dead time.
            //
            // Only fires once the entitlement is CONFIRMED. If the webhook hasn't landed, the
            // timeout branch below keeps them here with an explanation instead of dropping them
            // into the app to hit a paywall they already paid to avoid.
            window.location.href = "/app"
        }
    }

    Div(attrs = {
        style {
            property("background", TcCream.toString())
            property("font-family", FontBody)
            color(TcInk)
            property("min-height", "100vh")
            display(DisplayStyle.Flex); flexDirection(FlexDirection.Column)
            alignItems(AlignItems.Center); justifyContent(JustifyContent.Center)
            property("text-align", "center")
            padding(24.px); property("box-sizing", "border-box")
        }
    }) {
        Img(
            src = "/images/offer/logo.png",
            attrs = { style { width(220.px); marginBottom(24.px); borderRadius(8.px) }; attr("alt", "Fitrah Filter") }
        )
        // The burst is anchored to the headline (position:relative on the wrapper) rather than the
        // page, so it fires from the words rather than from an arbitrary corner.
        Div(attrs = { style { property("position", "relative") } }) {
            ConfettiBurst()
            H1(attrs = {
                style {
                    property("font-family", FontHead); fontSize(28.px); color(TcNavy)
                    property("position", "relative"); property("z-index", "1")
                }
            }) {
                Text("JazakumAllahu khairan, you're in!")
            }
        }

        if (!authState.isAuthenticated) {
            P(attrs = {
                style {
                    fontSize(16.px); property("max-width", "460px"); lineHeight("1.6")
                    property("opacity", "0.85"); property("margin-bottom", "24px")
                }
            }) {
                Text(
                    if (switchedToSignIn)
                        "You already have a Fitrah Filter account with this email. Sign in below " +
                            "and your membership will be added to it."
                    else
                        // Deliberately no price: this page outlives any particular offer, and a
                        // hardcoded "$9/month" would quietly start lying the day pricing changes.
                        "Your membership is confirmed. Create your account below to start using " +
                            "it, on the web and in the app."
                )
            }
            ThriveCartClaimCard(authViewModel, authState)
            return@Div
        }

        val settled = entitled
        // A leftover session for someone else (shared computer, previous buyer) would otherwise sit
        // here polling a stranger's account and reporting THEIR membership state. Deliberately an
        // offer to switch rather than a forced sign-out: the checkout email arrives in a URL that
        // anyone can craft, so acting on it automatically would let a hand-made link boot people
        // out of their session. The /register page can force it because Stripe verifies the email;
        // nothing verifies this one.
        val signedInEmail = authState.user?.email?.trim()?.lowercase()
        val mismatched = !settled && paidEmail.isNotBlank() && signedInEmail != null &&
            signedInEmail != paidEmail.trim().lowercase()
        if (mismatched) {
            P(attrs = {
                style {
                    fontSize(15.px); property("max-width", "440px"); lineHeight("1.6")
                    color(TcNavy); property("margin-bottom", "8px")
                }
            }) {
                Text(
                    "You're signed in as $signedInEmail, but this purchase was made with " +
                        "${paidEmail.trim().lowercase()}. If that's not your account, sign out and " +
                        "create one with the email you paid with."
                )
            }
            Button(attrs = {
                onClick { authViewModel.signOut() }
                style {
                    property("font-family", FontBody); fontSize(15.px); fontWeight(600)
                    padding(10.px, 20.px); marginBottom(20.px)
                    property("background", "transparent"); color(TcTeal)
                    property("border", "1px solid rgba(13,118,128,0.4)")
                    property("border-radius", ButtonRadius); property("cursor", "pointer")
                }
            }) { Text("Sign out") }
        }
        P(attrs = {
            style {
                fontSize(16.px); property("max-width", "440px"); lineHeight("1.6")
                property("opacity", "0.85"); property("margin-bottom", "28px")
            }
        }) {
            Text(
                when {
                    settled -> "Your membership is active. Taking you into Fitrah Filter…"
                    polling -> "Activating your membership…"
                    pollTimedOut ->
                        "We haven't seen your membership activate yet on " +
                            "${authState.user?.email ?: "this account"}. Make sure you signed up " +
                            "with the same email you used at checkout" +
                            (orderId?.let { " (order #$it)" } ?: "") +
                            ". Still stuck? Email support@fitrahfilter.com and we'll sort it out."
                    else -> "Checking your membership…"
                }
            )
        }
        // Shown for the ~2.6s before the redirect fires, and it's the way out if that redirect is
        // ever blocked (pop-up blockers, an in-app browser) — so nobody who paid can get stranded
        // on this screen with no visible next step.
        if (settled) {
            Button(attrs = {
                onClick { window.location.href = "/app" }
                style {
                    property("font-family", FontBody); fontSize(16.px); fontWeight(700)
                    padding(15.px, 34.px)
                    property("background", TcGold.toString()); color(TcWhite)
                    property("border", "none"); property("border-radius", ButtonRadius)
                    property("cursor", "pointer"); marginBottom(20.px)
                }
            }) { Text("Start using Fitrah Filter") }
            // Download badges stay below: most buyers will want the app on their phone, and this
            // is the only screen in the funnel that reliably gets their attention.
        }

        Div(attrs = {
            style {
                display(DisplayStyle.Flex); justifyContent(JustifyContent.Center)
                alignItems(AlignItems.Center); property("gap", "12px"); marginBottom(20.px)
            }
        }) {
            A(
                href = Utm.APP_STORE_URL,
                attrs = {
                    attr("target", "_blank"); attr("rel", "noopener noreferrer")
                    style { display(DisplayStyle.InlineBlock); property("line-height", "0") }
                }
            ) {
                Img(
                    src = "https://tools.applemediaservices.com/api/badges/download-on-the-app-store/black/en-us?size=250x83",
                    attrs = { attr("alt", "Download on the App Store"); style { height(40.px) } }
                )
            }
            A(
                href = Utm.playStoreUrl(source = "thrivecart_thank_you", medium = "thank_you_badge"),
                attrs = {
                    attr("target", "_blank"); attr("rel", "noopener noreferrer")
                    style { display(DisplayStyle.InlineBlock); property("line-height", "0") }
                }
            ) {
                Img(
                    src = "https://play.google.com/intl/en_us/badges/static/images/badges/en_badge_web_generic.png",
                    attrs = { attr("alt", "Get it on Google Play"); style { height(58.px) } }
                )
            }
        }

        A(
            href = "https://fitrahfilter.com",
            attrs = {
                attr("target", "_blank"); attr("rel", "noopener noreferrer")
                style { fontSize(14.px); color(TcTeal); property("text-decoration", "underline") }
            }
        ) { Text("Visit fitrahfilter.com") }
    }

    // REQUIRED, not optional polish. Email confirmations are ON, so signUpWithEmail returns no
    // session — AuthViewModel flips `awaitingVerification` and waits for a 6-digit code. Without
    // this modal the buyer taps "Create My Account", the account IS created, and then nothing
    // visible happens: no session means no entitlement poll and no redirect, so they sit on a form
    // that looks broken. Mounted at the root so it overlays whatever state the page is in.
    EmailVerificationModal(authViewModel)
}

/**
 * A one-shot confetti burst behind the headline.
 *
 * Hand-rolled rather than pulled from a CDN: this page is the first thing a paying customer sees,
 * and a third-party script that fails to load (or is blocked) would be a broken first impression
 * for a few dozen coloured squares. Pure CSS keyframes + absolutely-positioned divs, no JS loop,
 * no canvas — the browser animates it off the main thread and it cleans itself up.
 *
 * Each piece gets its own angle, distance, colour and delay so the burst doesn't look mechanical.
 * `prefers-reduced-motion` suppresses it entirely.
 */
@Composable
private fun ConfettiBurst(pieces: Int = 180) {
    InjectStyles(
        "tc-confetti",
        """
        .tc-confetti-wrap { position: absolute; top: 0; left: 50%; width: 0; height: 0;
            pointer-events: none; z-index: 0; }
        /* ONE continuous flight path, not two spliced segments. The arc is a sampled parabola:
           horizontal decelerates (air drag), vertical rises to a peak and accelerates back down
           (gravity), and because every stop is on the same curve there is no seam to notice.
           Timing is linear because the SHAPE carries the physics — easing on top of a parabola is
           what made the previous version stall at the join. */
        .tc-confetti-piece { position: absolute; top: 0; left: 0;
            will-change: transform, opacity;
            animation: tc-confetti-fly var(--dur) linear forwards; }
        @keyframes tc-confetti-fly {
            0%   { transform: translate(0,0) scale(.35); opacity: 1; }
            15%  { transform: translate(calc(var(--dx) * .36),
                                        calc(var(--dy) * .51 + var(--fall) * .02)) scale(1); }
            30%  { transform: translate(calc(var(--dx) * .62),
                                        calc(var(--dy) * .84 + var(--fall) * .09)) scale(1); }
            50%  { transform: translate(calc(var(--dx) * .84),
                                        calc(var(--dy) + var(--fall) * .25)) scale(1); }
            70%  { transform: translate(calc(var(--dx) * .94),
                                        calc(var(--dy) * .84 + var(--fall) * .49)) scale(1); }
            85%  { transform: translate(calc(var(--dx) * .98),
                                        calc(var(--dy) * .51 + var(--fall) * .72)) scale(1);
                   opacity: 1; }
            100% { transform: translate(var(--dx), var(--fall)) scale(1); opacity: 0; }
        }
        /* Tumbling lives on a CHILD with its own duration, so a piece's spin is unrelated to its
           flight time. Sharing one clock is what made 180 pieces move as one object. */
        .tc-confetti-piece > b { display: block; width: 100%; height: 100%;
            will-change: transform;
            animation: tc-confetti-spin var(--spin) linear infinite; }
        @keyframes tc-confetti-spin {
            0%   { transform: rotate(0deg) rotateY(0deg); }
            100% { transform: rotate(var(--rot)) rotateY(720deg); }
        }
        @media (prefers-reduced-motion: reduce) { .tc-confetti-wrap { display: none; } }
        """.trimIndent()
    )
    // Saturated party colours, deliberately NOT the site's muted brand palette — this is the one
    // moment on the site that should feel loud.
    // The pieces are built IMPERATIVELY, exactly once, and Compose never manages them.
    //
    // Why not just emit them as composables: this page recomposes repeatedly (the query-string
    // effect fills in the buyer's details, then isLoading, isAuthenticated, polling and entitled
    // all flip in turn), and the sign-up transition restructures this subtree. Whenever Compose
    // recreates a DOM node its CSS animation starts again from zero — so the burst fired a second
    // time after "Create My Account". Remembering the random values wasn't enough: the values were
    // stable, the NODES weren't. Owning the nodes ourselves is what makes a replay impossible.
    Div(attrs = { classes("tc-confetti-wrap"); id(CONFETTI_ROOT_ID) })
    LaunchedEffect(Unit) {
        // Module-level flag, NOT remember{} and NOT a check on the container's children: when the
        // sign-up transition rebuilds this subtree, Compose throws away the container and hands us
        // a fresh empty one, so both of those reset and the burst fired again. A plain top-level
        // var outlives that, which is the whole point.
        if (didConfettiPop) return@LaunchedEffect
        didConfettiPop = true
        val root = document.getElementById(CONFETTI_ROOT_ID) ?: return@LaunchedEffect
        repeat(pieces) { i ->
            val p = ConfettiPiece.random(i)
            val piece = document.createElement("div") as HTMLElement
            piece.className = "tc-confetti-piece"
            piece.style.cssText =
                "--dx:${p.dx}px;--dy:${p.dy}px;--fall:${p.fall}px;--rot:${p.rot}deg;" +
                    "--dur:${p.dur}ms;--spin:${p.spin}ms;" +
                    "width:${p.w}px;height:${p.h}px;animation-delay:${p.delay}ms"
            val inner = document.createElement("b") as HTMLElement
            inner.style.cssText =
                "background:${p.color};border-radius:${if (p.round) "50%" else "2px"};" +
                    "animation-delay:${p.spinDelay}ms"
            piece.appendChild(inner)
            root.appendChild(piece)
        }
    }
}

private const val CONFETTI_ROOT_ID = "tc-confetti-root"

/** Fires once per page load, full stop. Deliberately outside composition so nothing Compose does
 *  — recomposition, disposal, rebuilding this subtree on sign-up — can reset it. */
private var didConfettiPop = false

/** One piece's randomised flight, rolled once so recomposition can't restart the animation. */
private data class ConfettiPiece(
    val dx: Double, val dy: Double, val fall: Int, val rot: Int,
    val dur: Int, val spin: Int, val delay: Int, val spinDelay: Int,
    val w: Int, val h: Int, val color: String, val round: Boolean,
) {
    companion object {
        private val COLORS = listOf(
            "#FF2E63", "#FFD400", "#00E5FF", "#7CFF3D", "#FF6D00", "#B14BFF",
            "#00FF9D", "#FF2ECC", "#2E7BFF", "#FFF04A", "#FF4757", "#18FFD5",
        )

        fun random(i: Int): ConfettiPiece {
            // Full circle, biased upward so it launches rather than rains. Wide distance spread so
            // the burst has a dense core and a few pieces flung right across the screen.
            val angle = Random.nextDouble(0.0, 2 * PI)
            val distance = Random.nextDouble(140.0, 620.0)
            val w = Random.nextInt(8, 20)
            return ConfettiPiece(
                dx = cos(angle) * distance,
                dy = sin(angle) * distance - Random.nextDouble(120.0, 320.0),
                // Generous fall: the back half of the flight is gravity, and a short drop is what
                // made pieces look like they stopped mid-air.
                fall = Random.nextInt(520, 1100),
                rot = Random.nextInt(-900, 900),
                // Own flight time and spin rate per piece. Uniform timing is what made 180 pieces
                // read as one mechanical object rather than a scatter.
                dur = Random.nextInt(1500, 3400),
                spin = Random.nextInt(400, 1400),
                delay = Random.nextInt(0, 240),
                spinDelay = Random.nextInt(0, 400),
                w = w,
                h = w * Random.nextInt(1, 4),   // squares, rectangles and streamers
                color = COLORS[i % COLORS.size],
                round = i % 5 == 0,
            )
        }
    }
}

private fun StyleScope.tcInputStyle() {
    width(100.percent)
    padding(14.px, 16.px)
    fontSize(15.px)
    property("font-family", FontBody)
    borderRadius(6.px)
    property("border", "1px solid rgba(16,46,71,0.2)")
    property("outline", "none")
    property("box-sizing", "border-box")
    marginBottom(12.px)
}

/** Account creation card — same shape as ClaimAccountCard on /register, minus the OAuth
 *  "willNavigateAway" session-storage flag (no wrong-account guard is needed here: unlike the
 *  Stripe flow, nothing on this page trusts the checkout email enough to sign anyone out over
 *  it — see the file doc comment). No Apple button, matching /register's reasoning: untested on
 *  web, and this is not the screen to find that out on. */
@Composable
private fun ThriveCartClaimCard(viewModel: AuthViewModel, uiState: AuthUiState) {
    Div(attrs = {
        style {
            property("background", TcWhite.toString())
            property("border-radius", "18px")
            padding(24.px)
            property("max-width", "380px"); width(100.percent)
            property("box-sizing", "border-box")
            property("box-shadow", "0 18px 50px -18px rgba(16,46,71,0.28)")
            property("text-align", "left")
        }
    }) {
        if (uiState.isSignUp) {
            Div(attrs = { style { display(DisplayStyle.Flex); property("gap", "10px") } }) {
                Div(attrs = { style { property("flex", "1"); property("min-width", "0") } }) {
                    Input(type = InputType.Text) {
                        value(uiState.firstName)
                        onInput { viewModel.onFirstNameChanged(it.value) }
                        attr("placeholder", "First name")
                        style { tcInputStyle() }
                    }
                }
                Div(attrs = { style { property("flex", "1"); property("min-width", "0") } }) {
                    Input(type = InputType.Text) {
                        value(uiState.lastName)
                        onInput { viewModel.onLastNameChanged(it.value) }
                        attr("placeholder", "Last name")
                        style { tcInputStyle() }
                    }
                }
            }
        }

        Input(type = InputType.Email) {
            value(uiState.email)
            onInput { viewModel.onEmailChanged(it.value) }
            attr("placeholder", "Your email address")
            style { tcInputStyle() }
        }
        Input(type = InputType.Password) {
            value(uiState.password)
            onInput { viewModel.onPasswordChanged(it.value) }
            attr("placeholder", if (uiState.isSignUp) "Choose a password" else "Your password")
            style { tcInputStyle() }
        }

        uiState.error?.let { err ->
            P(attrs = { style { color(rgb(200, 50, 50)); fontSize(13.px); property("margin", "0 0 10px") } }) { Text(err) }
        }

        Button(attrs = {
            if (!uiState.isSubmitting) onClick { viewModel.onSubmit() }
            style {
                property("font-family", FontBody)
                fontSize(16.px); fontWeight(700)
                padding(15.px, 28.px)
                property("border", "none")
                property("border-radius", ButtonRadius)
                property("cursor", if (uiState.isSubmitting) "default" else "pointer")
                property("opacity", if (uiState.isSubmitting) "0.75" else "1")
                width(100.percent)
                property("background", TcGold.toString())
                color(TcWhite)
            }
        }) {
            Text(
                when {
                    uiState.isSubmitting -> "Please wait…"
                    uiState.isSignUp -> "Create My Account"
                    else -> "Sign In"
                }
            )
        }

        Div(attrs = {
            style {
                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                property("gap", "12px"); property("margin", "14px 0")
            }
        }) {
            Div(attrs = { style { property("flex", "1"); height(1.px); property("background", "rgba(16,46,71,0.15)") } })
            Span(attrs = { style { fontSize(13.px); property("opacity", "0.6") } }) { Text("or") }
            Div(attrs = { style { property("flex", "1"); height(1.px); property("background", "rgba(16,46,71,0.15)") } })
        }

        Button(attrs = {
            onClick {
                // Come back to THIS page (not Supabase's default bare-origin redirect), so the OAuth
                // round-trip lands back on the poll rather than the app root with nothing resolved.
                viewModel.signInWithGoogle(window.location.origin + "/thrivecart-thank-you")
            }
            style {
                width(100.percent); padding(12.px); fontSize(15.px); fontWeight(600)
                property("background", TcWhite.toString()); color(TcInk)
                property("border", "1px solid rgba(16,46,71,0.2)")
                property("border-radius", ButtonRadius)
                property("cursor", "pointer"); property("box-sizing", "border-box")
                display(DisplayStyle.Flex); alignItems(AlignItems.Center)
                justifyContent(JustifyContent.Center); property("gap", "10px")
            }
            if (uiState.isSubmitting) attr("disabled", "true")
        }) {
            Img(src = "https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg", attrs = {
                style { width(20.px); height(20.px) }
            })
            Text("Continue with Google")
        }
    }
}
